/* Free jewelry case: one per set (snippets/free-case.liquid has the settings).

   Timing: the case is put in place right after the theme's own cart request (add, change, update) and before the theme
   draws the cart, so the drawer opens on the final cart. A change request's reply already holds the whole cart, so
   nothing more is fetched; after an add the cart is read once (a stored copy could be out of date when taps come fast).
   Cases are only written when the count is wrong, and checking that new cases came out free happens afterwards.
   Writes don't depend on how Shopify split the case lines: to go down, all case lines are taken out in one request and
   the right number added back in one. The theme:cart:change event and a check on page load cover anything else.

   Rules: the cases follow the cart (two sets, two cases; one set removed, one case). When the last set goes, one case
   stays at full price, or none (theme setting); not if the customer has a case of their own in the cart. A case
   the customer adds as a normal product is theirs and costs money: only one case per set is free. The case is made free by the store's Buy X get Y discount; when
   Shopify splits the case into several lines, those lines count together. A case the script added that doesn't
   come out free (a discount is off or doesn't cover a product) is taken out again and not re-added for that count, so
   nobody pays for a case the script added. Lines marked _Gifted come from the old EG app and are replaced.

   The case rows' - and + (rows marked data-case-ours-qty): + adds a case of the customer's own, which costs money; -
   takes cases out, the customer's paid ones first. A free case taken out stays out for the visit (not added back), until
   the sets no longer cover it. */
(() => {
  const configEl = document.getElementById('FreeCaseConfig');
  if (!configEl) return;
  const config = JSON.parse(configEl.textContent);
  const sets = new Set(config.sets);
  const MARK = '_free_case';
  const nativeFetch = window.fetch.bind(window);
  const CART_WRITE = /\/cart\/(add|change|update)(\.js)?(\?|$)/;

  // How many cases the discounts actually made free for a given count ({ needed, allowed }); kept for the visit so a
  // reload doesn't add the uncovered cases again.
  const LIMIT_KEY = 'free-case-limit';
  let limit = null;
  try {
    limit = JSON.parse(sessionStorage.getItem(LIMIT_KEY));
  } catch (error) {
    limit = null;
  }

  // Free cases the customer took out themselves (kept for the visit).
  const DECLINED_KEY = 'free-case-declined';
  let declined = 0;
  try {
    declined = Number(sessionStorage.getItem(DECLINED_KEY)) || 0;
  } catch (error) {
    declined = 0;
  }
  const setDeclined = (value) => {
    declined = Math.max(0, value);
    try {
      sessionStorage.setItem(DECLINED_KEY, String(declined));
    } catch (error) {
      // private mode: only for this page
    }
  };

  const request = (url, body) =>
    nativeFetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then((response) => (response.ok ? response.json() : Promise.reject(new Error(`cart ${response.status}`))));

  const getCart = () => request(`${config.cartUrl}.js`);
  const removeLines = (lines) => request(`${config.cartUrl}/update.js`, { updates: Object.fromEntries(lines.map((line) => [line.key, 0])) });
  const addCases = (quantity) => request(`${config.addUrl}.js`, { items: [{ id: config.caseVariant, quantity, properties: { [MARK]: 'true' } }] });

  const ours = (cart) => cart.items.filter((item) => item.variant_id === config.caseVariant && item.properties && item.properties[MARK]);
  const ownCases = (cart) => cart.items.filter((item) => item.variant_id === config.caseVariant && !(item.properties && (item.properties[MARK] || item.properties._Gifted)));
  const oldAppLines = (cart) => cart.items.filter((item) => item.variant_id === config.caseVariant && item.properties && item.properties._Gifted);
  const count = (lines) => lines.reduce((sum, item) => sum + item.quantity, 0);
  const paidUnits = (lines) =>
    lines.reduce((sum, item) => sum + (item.original_price > 0 ? Math.ceil(item.final_line_price / item.original_price) : 0), 0);

  const casesNeeded = (cart) =>
    cart.items.reduce((sum, item) => sum + (item.variant_id !== config.caseVariant && sets.has(item.product_id) ? item.quantity : 0), 0);

  // Brings the free cases in line with the cart. Returns true when it changed the cart.
  // known: a cart to work from instead of fetching one. fast: the theme is waiting, so the free check runs afterwards.
  async function sync({ known = null, fast = false } = {}) {
    const cart = known || (await getCart());
    const needed = casesNeeded(cart);
    if (declined > needed) setDeclined(needed);
    const earned = needed - declined;
    const target = limit && limit.needed === needed ? Math.min(earned, limit.allowed) : earned;
    const oldApp = oldAppLines(cart);
    const inCart = count(ours(cart)) + count(oldApp);

    // The cases follow the cart. Only when nothing earns a case any more does one case stay, at full price, and only
    // if the customer has no case of their own.
    let want = target;
    if (needed === 0 && inCart > 0 && config.keepUnearned && !count(ownCases(cart))) want = 1;
    if (want === inCart && !oldApp.length) return false;

    // Going up: add the difference. Going down (or old app lines): take every case line out, add the right number back.
    let toAdd = want - inCart;
    if (toAdd < 0 || oldApp.length) {
      await removeLines([...ours(cart), ...oldApp]);
      toAdd = want;
    }
    if (toAdd > 0) {
      await addCases(toAdd);
      const check = () => verify();
      if (fast) setTimeout(() => enqueue(check).then((changed) => changed && refreshCart()), 1200);
      else await check();
    }
    return true;
  }

  // Safety net. While a case is earned, every case of ours should be free; any that costs money wasn't covered by a
  // discount and is taken out (and not re-added for this count). Returns true when it changed the cart.
  async function verify() {
    const cart = await getCart();
    const needed = casesNeeded(cart);
    if (needed - declined <= 0) return false;
    // Shopify may put the free case on the customer's own case line, so count what's paid over all case lines.
    const uncovered = paidUnits([...ours(cart), ...ownCases(cart)]) - count(ownCases(cart));
    if (uncovered <= 0) return false;
    limit = { needed, allowed: Math.max(0, count(ours(cart)) - uncovered) };
    try {
      sessionStorage.setItem(LIMIT_KEY, JSON.stringify(limit));
    } catch (error) {
      // private mode: the limit only lasts for this page
    }
    await removeLines(ours(cart));
    if (limit.allowed > 0) await addCases(limit.allowed);
    return true;
  }

  // One task at a time, in order.
  let queue = Promise.resolve(false);
  const enqueue = (task = () => sync()) => {
    queue = queue.then(task, task).catch((error) => {
      console.warn('Free jewelry case:', error);
      return false;
    });
    return queue;
  };

  const refreshCart = () => {
    if (config.onCartPage) window.location.reload();
    else document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  };

  // The cart right after a theme request: a change/update reply is the whole cart; after an add it is read.
  const cartAfter = (reply) => (reply && Array.isArray(reply.items) && 'item_count' in reply ? reply : null);

  // - or + on a case row: the theme sends /cart/change { line, quantity } for the row; answered here with the cart.
  const caseRowChange = (url, init) => {
    if (!/\/cart\/change(\.js)?(\?|$)/.test(url) || !init || typeof init.body !== 'string' || !document.querySelector) return null;
    let body;
    try {
      body = JSON.parse(init.body);
    } catch (error) {
      return null;
    }
    const row = body.line && document.querySelector(`[data-item-index="${Number(body.line)}"][data-case-ours-qty]`);
    if (!row) return null;
    const diff = Number(body.quantity) - Number(row.dataset.caseOursQty);
    return enqueue(async () => {
      if (diff > 0) {
        await request(`${config.addUrl}.js`, { items: [{ id: config.caseVariant, quantity: diff }] });
      } else if (diff < 0) {
        const cart = await getCart();
        let out = -diff;
        const updates = {};
        ownCases(cart).forEach((line) => {
          const take = Math.min(line.quantity, out);
          if (take > 0) updates[line.key] = line.quantity - take;
          out -= take;
        });
        if (Object.keys(updates).length) await request(`${config.cartUrl}/update.js`, { updates });
        if (out > 0) {
          const needed = casesNeeded(cart);
          if (needed > 0) setDeclined(Math.min(needed, declined + out));
          else await request(`${config.cartUrl}/update.js`, { updates: Object.fromEntries(ours(cart).map((line) => [line.key, 0])) });
        }
      }
      await sync();
      return false;
    }).then(() => nativeFetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } }));
  };

  // The theme's cart writes: put the case in place before the theme reads the cart back and draws it.
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const caseRow = caseRowChange(url, init);
    if (caseRow) return caseRow;
    const response = await nativeFetch(input, init);
    const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method === 'POST' && response.ok && CART_WRITE.test(url)) {
      const reply = await response.clone().json().catch(() => null);
      await enqueue(() => sync({ known: cartAfter(reply), fast: true }));
    }
    return response;
  };

  // Anything else (another app, another tab): fix up afterwards and redraw.
  let timer;
  document.addEventListener('theme:cart:change', () => {
    clearTimeout(timer);
    timer = setTimeout(() => enqueue().then((changed) => changed && refreshCart()), 150);
  });
  const outOfStep = config.keepUnearned && config.needed === 0 ? config.inCart > (config.ownCases ? 0 : 1) : config.needed !== config.inCart;
  if (outOfStep || config.oldAppLines) enqueue().then((changed) => changed && refreshCart());
})();
